<template>
  <div>
    <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
      <div>
        <h2 class="text-xl font-semibold text-secondary-800">{{ me ? me.name : "" }}</h2>
      </div>
    </div>

    <!-- plain summary row, like the sales tools cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" :class="licenses ? 'lg:grid-cols-4' : 'lg:grid-cols-3'">
      <div class="bg-white border border-secondary-200 rounded p-4">
        <p class="text-xs font-semibold text-secondary-500 uppercase tracking-wide">{{ $t("securone.nav.categories") }}</p>
        <p class="text-2xl font-semibold text-secondary-800 mt-1">{{ adminList.length }}</p>
      </div>
      <div class="bg-white border border-secondary-200 rounded p-4">
        <p class="text-xs font-semibold text-secondary-500 uppercase tracking-wide">{{ $t("securone.common.groups") }}</p>
        <p class="text-2xl font-semibold text-secondary-800 mt-1">{{ groups.length }}</p>
      </div>
      <div class="bg-white border border-secondary-200 rounded p-4">
        <p class="text-xs font-semibold text-secondary-500 uppercase tracking-wide">{{ $t("securone.overview.departments") }}</p>
        <p class="text-2xl font-semibold text-secondary-800 mt-1">{{ departments.length }}</p>
      </div>
      <!-- licences (Securdia admin): bought vs. basic users at the customer's units -->
      <div v-if="licenses" class="bg-white border border-secondary-200 rounded p-4" :title="$t('securone.overview.licenses_help')">
        <p class="text-xs font-semibold text-secondary-500 uppercase tracking-wide">{{ $t("securone.overview.licenses") }}</p>
        <div class="grid grid-cols-3 gap-2 mt-1">
          <div>
            <p class="text-2xl font-semibold text-secondary-800">{{ licenses.total === null ? "–" : licenses.total }}</p>
            <p class="text-xs text-secondary-500">{{ $t("securone.overview.licenses_total") }}</p>
          </div>
          <div>
            <p class="text-2xl font-semibold text-secondary-800">{{ licenses.used }}</p>
            <p class="text-xs text-secondary-500">{{ $t("securone.overview.licenses_used") }}</p>
          </div>
          <div>
            <p class="text-2xl font-semibold" :class="licenses.remaining !== null && licenses.remaining < 0 ? 'text-red-600' : 'text-secondary-800'">{{ licenses.remaining === null ? "–" : licenses.remaining }}</p>
            <p class="text-xs text-secondary-500">{{ $t("securone.overview.licenses_remaining") }}</p>
          </div>
        </div>
        <p v-if="licenses.total === null" class="text-xs text-secondary-500 mt-2">{{ $t("securone.overview.licenses_not_set") }}</p>
        <p v-else-if="licenses.remaining < 0" class="text-xs text-red-600 mt-2">{{ $tc("securone.overview.licenses_over", -licenses.remaining, { n: -licenses.remaining }) }}</p>
      </div>
    </div>

    <!-- free-text alarm: always available to customer users -->
    <div v-if="canRaiseFreeText" class="bg-white border border-secondary-200 border-l-4 border-l-securdia-blue rounded p-4 mb-6 flex flex-wrap items-center gap-4">
      <div class="w-10 h-10 rounded bg-blue-50 border border-blue-100 flex items-center justify-center text-securdia-blue flex-shrink-0"><BaseIcon icon="bullhorn" /></div>
      <div class="flex-1 min-w-0" style="min-width: 240px">
        <div class="text-sm font-semibold text-secondary-800">{{ $t("securone.freetext.overview_title") }}</div>
        <div class="text-xs text-secondary-500 mt-0.5 leading-relaxed">{{ $t("securone.freetext.overview_help") }}</div>
      </div>
      <router-link :to="{ name: 'securone_freetext' }" class="sd-btn sd-btn--primary">{{ $t("securone.freetext.overview_button") }}<BaseIcon icon="arrow-right" class="ml-2" /></router-link>
    </div>

    <template v-if="isPrincipal">
      <div class="flex flex-wrap items-end justify-between gap-3 mb-3">
        <h3 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t("securone.overview.reachable_title") }}</h3>
        <span class="text-xs text-secondary-500">{{ $t("securone.overview.reachable_help_send") }}</span>
      </div>
      <div v-if="reachable.length" class="bg-white border border-secondary-200 rounded divide-y divide-secondary-100 mb-4">
        <router-link v-for="c in reachable" :key="c._id" :to="{ name: 'securone_broadcast', params: { id: c._id } }" class="flex items-start gap-3 px-4 py-3 hover:bg-secondary-50">
          <div class="w-8 h-8 rounded bg-secondary-100 flex items-center justify-center text-secondary-600 flex-shrink-0"><BaseIcon :icon="categoryIcon(c.icon)" class="text-sm" /></div>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-secondary-800">{{ c.name }}</div>
            <div class="text-xs text-secondary-500 mt-0.5 leading-relaxed">{{ c.instruction }}</div>
          </div>
          <div class="text-xs text-secondary-500 whitespace-nowrap">{{ reachLine(c.reach) }}</div>
          <BaseIcon icon="chevron-right" class="text-secondary-400 mt-1" />
        </router-link>
      </div>
      <SoNote v-else tone="info" class="mb-4">{{ $t("securone.overview.nothing_reachable") }}</SoNote>
      <SoNote v-if="hiddenCount" tone="info" class="mb-3">
        <b class="font-semibold">{{ $tc("securone.overview.hidden_categories", hiddenCount, { n: hiddenCount }) }}</b>
        {{ $t("securone.overview.hidden_categories_help") }}
      </SoNote>
    </template>
    <SoNote v-else tone="info" class="mb-3">{{ $t("securone.overview.admin_hint") }}</SoNote>

    <SoNote tone="muted" icon="lock">{{ $t("securone.overview.never_alarm_centre") }}</SoNote>
  </div>
</template>

<script>
import SoNote from "./components/SoNote.vue";
import api from "./securone.api";
import { categoryIcon } from "./securone.helpers";

// Landing page: a plain summary row and - for a principal - the categories the
// signed-in account can reach (AR1). The rows link to the broadcast screen,
// where sending needs a deliberate hold; nothing goes out from this page
// itself, and no alarm is ever one stray click away.
export default {
  name: "SecurOneOverview",
  components: { SoNote },
  data() {
    return { adminList: [], reachable: [], groups: [] };
  },
  computed: {
    me() {
      return this.$store.getters["securone/me"];
    },
    kind() {
      return this.$store.getters["securone/kind"];
    },
    isPrincipal() {
      return this.$store.getters["securone/isPrincipal"];
    },
    licenses() {
      return this.$store.getters["securone/licenses"];
    },
    canRaiseFreeText() {
      return this.$store.getters["securone/canRaiseFreeText"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    myGroupIds() {
      return this.$store.getters["securone/myGroupIds"];
    },
    myGroups() {
      return this.groups.filter((g) => this.myGroupIds.includes(String(g._id)));
    },
    hiddenCount() {
      const reachableIds = this.reachable.map((c) => c._id);
      return this.adminList.filter((c) => c.isActive !== false && !reachableIds.includes(c._id)).length;
    },
    adminVersion() {
      return this.$store.state.securone.adminVersion;
    },
  },
  methods: {
    categoryIcon,
    reachLine(reach) {
      let line = this.$tc("securone.common.people_in_groups", reach.groups, { people: reach.people, groups: reach.groups });
      if (reach.sms) line += " · " + this.$tc("securone.common.sms_contacts_count", reach.sms, { n: reach.sms });
      return line;
    },
    async load() {
      try {
        const [adminList, groups, reachable] = await Promise.all([api.categories(), api.groups(), this.isPrincipal ? api.categories({ reachable: true }) : []]);
        this.adminList = adminList;
        this.groups = groups;
        this.reachable = reachable.filter((c) => c.isActive !== false);
      } catch (error) {
        this.handleError(error);
      }
    },
  },
  watch: {
    adminVersion() {
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.overview"), "securone_overview");
    this.load();
  },
};
</script>
